import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Package } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button, buttonVariants } from '../components/ui/button';
import { cn } from '../lib/utils';
import toast, { Toaster } from 'react-hot-toast';
import { getAuthApiUrl } from '../lib/apiConfig';

export interface OrderListItem {
  id: number;
  order_number: string;
  status: string;
  payment_status: string;
  total_amount: string;
  created_at: string;
  has_tracking: boolean;
}

type OrdersLocationState = { from?: string };

export default function ProfileOrders() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const backTarget =
    (location.state as OrdersLocationState | null)?.from ?? '/discover';
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(() => {
    const t = token ?? localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(t ? { Authorization: `Token ${t}` } : {}),
    };
  }, [token]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(getAuthApiUrl('/api/orders/'), {
        headers: authHeaders(),
      });
      if (res.status === 401 || res.status === 403) {
        toast.error('Please sign in to view orders');
        navigate('/');
        return;
      }
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        console.error('Orders list failed', res.status, errText);
        toast.error('Could not load orders. Please try again.');
        setOrders([]);
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.results ?? [];
      setOrders(list);
    } catch {
      toast.error('Network error');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, navigate]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  const formatMoney = (v: string) => {
    const n = Number(v);
    if (Number.isNaN(n)) return v;
    return `₹${n.toFixed(2)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'rgba(255, 255, 255, 0.1)',
            color: '#ffffff',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            backdropFilter: 'blur(8px)',
          },
        }}
      />

      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(backTarget)}
            className="hover:bg-gray-800/50"
            aria-label="Back to discover"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gradient">My orders</h1>
            <p className="text-gray-400 mt-1">Order ID, date, and details</p>
          </div>
        </div>

        {loading ? (
          <div className="cosmic-card p-10 text-center text-gray-400">Loading orders…</div>
        ) : orders.length === 0 ? (
          <div className="cosmic-card p-10 text-center space-y-4">
            <Package className="mx-auto text-gray-500" size={40} />
            <p className="text-gray-400">You have not placed any orders yet.</p>
            <Button variant="secondary" onClick={() => navigate('/discover')}>
              Discover products
            </Button>
          </div>
        ) : (
          <ul className="space-y-4">
            {orders.map((o) => (
              <li key={o.id} className="cosmic-card p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-white font-semibold">{o.order_number}</p>
                    <p className="text-sm text-gray-400">{formatDate(o.created_at)}</p>
                    <p className="text-sm text-electric-cyan">{formatMoney(o.total_amount)}</p>
                    <p className="text-xs text-gray-500 capitalize">
                      {o.status.replace(/_/g, ' ')} · {o.payment_status}
                    </p>
                  </div>
                  <Link
                    to={`/profile/orders/${o.id}`}
                    state={location.state}
                    className={cn(buttonVariants({ variant: 'secondary' }), 'inline-flex gap-2 shrink-0')}
                  >
                    See details
                    <ChevronRight size={16} />
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
